// useMapInteraction.ts -- Pan/zoom/drag/hover interaction hook with entity bus integration.
// Per D-32, D-33, D-34, D-35, D-36, UI-SPEC Interaction Contract.
// Receives canvasRef from MapSurface for event listeners + hit-testing.
// Receives simulationRef from MapSurface for drag reheat (node.fx/fy, alpha restart).
// Calls bus.hover(id)/bus.unhover() on node hover for cross-surface entity highlighting.

import { useState, useEffect, useCallback, useRef } from 'react';
import type { GraphNode, Transform, MapInteractionState, MapSimulation } from './types';

// D-33: Zoom clamp range
const MIN_ZOOM = 0.05;
const MAX_ZOOM = 3.0;
const INITIAL_ZOOM = 0.3;
const ZOOM_STEP = 0.15;
const ZOOM_ANIM_MS = 200;

// D-35: Drag detection threshold (< 3px = click, not drag)
const DRAG_THRESHOLD = 3;

interface UseMapInteractionReturn {
  state: MapInteractionState;
  zoomIn: () => void;
  zoomOut: () => void;
  resetZoom: () => void;
  setTransform: (t: Transform) => void;
  focusOnNodes: (nodeIds: string[]) => void;
}

/** Hit-test against nodes in world-space coordinates */
function hitTest(
  clientX: number,
  clientY: number,
  nodes: GraphNode[],
  transform: Transform,
  canvas: HTMLCanvasElement,
): GraphNode | null {
  const rect = canvas.getBoundingClientRect();
  const canvasX = (clientX - rect.left - transform.x) / transform.k;
  const canvasY = (clientY - rect.top - transform.y) / transform.k;
  // Reverse iterate: nodes rendered last are visually on top
  for (let i = nodes.length - 1; i >= 0; i--) {
    const n = nodes[i];
    const dx = canvasX - (n.x ?? 0);
    const dy = canvasY - (n.y ?? 0);
    const hitRadius = n.r + 4;
    if (dx * dx + dy * dy <= hitRadius * hitRadius) return n;
  }
  return null;
}

export function useMapInteraction(
  canvasRef: React.RefObject<HTMLCanvasElement | null>,
  // The canvas ELEMENT as state (not just the ref): MapCanvas unmounts/remounts across the
  // Graph/List toggle, and a ref mutation alone never re-runs the listener effect — listeners
  // would stay on the discarded canvas and the rebuilt one would be inert (bug-451).
  canvasEl: HTMLCanvasElement | null,
  simulationRef: React.MutableRefObject<MapSimulation | null>,
  nodesRef: React.MutableRefObject<GraphNode[]>,
  adjacency: Map<string, Set<string>>,
  onNodeClick: (id: string) => void,
  onNodeRightClick: (id: string, x: number, y: number) => void,
): UseMapInteractionReturn {
  const [transform, setTransform] = useState<Transform>({ x: 0, y: 0, k: INITIAL_ZOOM });
  const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(null);
  const [draggedNodeId, setDraggedNodeId] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isPanning, setIsPanning] = useState(false);

  // Refs for event handlers (avoids stale closures)
  const transformRef = useRef(transform);
  transformRef.current = transform;

  const hoveredRef = useRef(hoveredNodeId);
  hoveredRef.current = hoveredNodeId;

  // Drag tracking refs
  const dragStartRef = useRef<{ x: number; y: number; node: GraphNode | null }>({ x: 0, y: 0, node: null });
  const hasDraggedRef = useRef(false);
  const panStartRef = useRef<{ x: number; y: number; tx: number; ty: number }>({ x: 0, y: 0, tx: 0, ty: 0 });

  // Smooth zoom animation ref
  const zoomAnimRef = useRef<number | null>(null);
  const centeredRef = useRef(false);

  // D-33: Animated zoom toward/from a target scale
  const animateZoom = useCallback((targetK: number, focalX?: number, focalY?: number) => {
    if (zoomAnimRef.current !== null) cancelAnimationFrame(zoomAnimRef.current);
    const startK = transformRef.current.k;
    const startX = transformRef.current.x;
    const startY = transformRef.current.y;
    const clampedK = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, targetK));

    // Default focal point: center of canvas
    const canvas = canvasRef.current;
    let fx = focalX ?? (canvas ? canvas.width / 2 : 0);
    let fy = focalY ?? (canvas ? canvas.height / 2 : 0);

    // Compute target pan offset to zoom around focal point
    const targetX = fx - (fx - startX) * (clampedK / startK);
    const targetY = fy - (fy - startY) * (clampedK / startK);

    const startTime = performance.now();
    function step(now: number) {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / ZOOM_ANIM_MS, 1);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      const k = startK + (clampedK - startK) * eased;
      const x = startX + (targetX - startX) * eased;
      const y = startY + (targetY - startY) * eased;
      setTransform({ x, y, k });
      if (progress < 1) {
        zoomAnimRef.current = requestAnimationFrame(step);
      } else {
        zoomAnimRef.current = null;
      }
    }
    zoomAnimRef.current = requestAnimationFrame(step);
  }, [canvasRef]);

  const zoomIn = useCallback(() => {
    animateZoom(transformRef.current.k + ZOOM_STEP);
  }, [animateZoom]);

  const zoomOut = useCallback(() => {
    animateZoom(transformRef.current.k - ZOOM_STEP);
  }, [animateZoom]);

  const resetZoom = useCallback(() => {
    animateZoom(1);
  }, [animateZoom]);

  // D-39: Focus camera on specific nodes (chat-driven graph navigation)
  const focusOnNodes = useCallback((nodeIds: string[]) => {
    const nodes = nodesRef.current;
    const idSet = new Set(nodeIds);
    const targets = nodes.filter(n => idSet.has(n.id));
    if (targets.length === 0) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    // Compute bounding box
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const n of targets) {
      const nx = n.x ?? 0;
      const ny = n.y ?? 0;
      minX = Math.min(minX, nx - n.r);
      minY = Math.min(minY, ny - n.r);
      maxX = Math.max(maxX, nx + n.r);
      maxY = Math.max(maxY, ny + n.r);
    }

    const bboxW = maxX - minX;
    const bboxH = maxY - minY;
    const padding = 60;
    const rect = canvas.getBoundingClientRect();
    const cw = rect.width;
    const ch = rect.height;

    // Scale to fit bounding box + padding
    const scaleX = (cw - padding * 2) / Math.max(bboxW, 1);
    const scaleY = (ch - padding * 2) / Math.max(bboxH, 1);
    const targetK = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, Math.min(scaleX, scaleY)));

    // Center on bounding box
    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;
    const targetX = cw / 2 - centerX * targetK;
    const targetY = ch / 2 - centerY * targetK;

    // Animate to target (600ms per UI-SPEC)
    if (zoomAnimRef.current !== null) cancelAnimationFrame(zoomAnimRef.current);
    const startK = transformRef.current.k;
    const startX = transformRef.current.x;
    const startY = transformRef.current.y;
    const startTime = performance.now();
    const duration = 600;

    function step(now: number) {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setTransform({
        x: startX + (targetX - startX) * eased,
        y: startY + (targetY - startY) * eased,
        k: startK + (targetK - startK) * eased,
      });
      if (progress < 1) {
        zoomAnimRef.current = requestAnimationFrame(step);
      } else {
        zoomAnimRef.current = null;
      }
    }
    zoomAnimRef.current = requestAnimationFrame(step);
  }, [canvasRef, nodesRef]);

  // Attach all event listeners to canvas element. Keyed on canvasEl so the wiring re-runs
  // every time the canvas element is replaced (e.g. List → Graph remount), not just once.
  useEffect(() => {
    const canvas = canvasEl;
    if (!canvas) return;

    if (!centeredRef.current) {
      centeredRef.current = true;
      const rect = canvas.getBoundingClientRect();
      const cx = rect.width / 2;
      const cy = rect.height / 2;
      setTransform({ x: cx * (1 - INITIAL_ZOOM), y: cy * (1 - INITIAL_ZOOM), k: INITIAL_ZOOM });
    }

    let panning = false;
    let panClickX = 0;
    let panClickY = 0;

    function handleMouseMove(e: MouseEvent) {
      if (panning) {
        const dx = e.clientX - panStartRef.current.x;
        const dy = e.clientY - panStartRef.current.y;
        setTransform({
          ...transformRef.current,
          x: panStartRef.current.tx + dx,
          y: panStartRef.current.ty + dy,
        });
        return;
      }

      // Hover hit-test
      const t = transformRef.current;
      const hit = hitTest(e.clientX, e.clientY, nodesRef.current, t, canvas!);
      const newHoverId = hit?.id ?? null;

      if (newHoverId !== hoveredRef.current) {
        setHoveredNodeId(newHoverId);
      }

      canvas!.style.cursor = hit ? 'pointer' : 'grab';
    }

    function handleMouseDown(e: MouseEvent) {
      if (e.button !== 0 && e.button !== 1) return;

      const t = transformRef.current;
      const hit = hitTest(e.clientX, e.clientY, nodesRef.current, t, canvas!);

      if (hit && e.button === 0) {
        // Node drag
        let dragged = false;
        const startX = e.clientX;
        const startY = e.clientY;

        const onMove = (ev: MouseEvent) => {
          const dx = ev.clientX - startX;
          const dy = ev.clientY - startY;
          if (!dragged && Math.sqrt(dx * dx + dy * dy) > DRAG_THRESHOLD) {
            dragged = true;
            setIsDragging(true);
            setDraggedNodeId(hit.id);
          }
          if (dragged) {
            const rect = canvas!.getBoundingClientRect();
            const ct = transformRef.current;
            hit.fx = (ev.clientX - rect.left - ct.x) / ct.k;
            hit.fy = (ev.clientY - rect.top - ct.y) / ct.k;
            if (simulationRef.current) {
              simulationRef.current.alpha(0.1).restart();
            }
          }
        };

        const onUp = (ev: MouseEvent) => {
          window.removeEventListener('mousemove', onMove);
          window.removeEventListener('mouseup', onUp);
          if (dragged) {
            hit.fx = null;
            hit.fy = null;
            if (simulationRef.current) {
              simulationRef.current.alpha(0.1).restart();
            }
            setIsDragging(false);
            setDraggedNodeId(null);
          } else {
            onNodeClick(hit.id);
          }
        };

        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
      } else {
        // Pan
        panning = true;
        setIsPanning(true);
        panClickX = e.clientX;
        panClickY = e.clientY;
        panStartRef.current = {
          x: e.clientX,
          y: e.clientY,
          tx: transformRef.current.x,
          ty: transformRef.current.y,
        };
        canvas!.style.cursor = 'grabbing';

        const onMove = (ev: MouseEvent) => {
          const dx = ev.clientX - panStartRef.current.x;
          const dy = ev.clientY - panStartRef.current.y;
          setTransform({
            ...transformRef.current,
            x: panStartRef.current.tx + dx,
            y: panStartRef.current.ty + dy,
          });
        };

        const onUp = () => {
          panning = false;
          setIsPanning(false);
          canvas!.style.cursor = 'grab';
          window.removeEventListener('mousemove', onMove);
          window.removeEventListener('mouseup', onUp);
        };

        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
      }
    }


    // D-33: Zoom via scroll wheel (focal point zoom)
    function handleWheel(e: WheelEvent) {
      e.preventDefault();
      const t = transformRef.current;
      const rect = canvas!.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      // Compute new scale
      const delta = -e.deltaY * 0.003;
      const newK = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, t.k * (1 + delta)));

      // Zoom around cursor position (focal point)
      const ratio = newK / t.k;
      const newX = mouseX - (mouseX - t.x) * ratio;
      const newY = mouseY - (mouseY - t.y) * ratio;

      setTransform({ x: newX, y: newY, k: newK });
    }

    // D-36: Right-click opens detail panel
    function handleContextMenu(e: MouseEvent) {
      e.preventDefault();
      const t = transformRef.current;
      const hit = hitTest(e.clientX, e.clientY, nodesRef.current, t, canvas!);
      if (hit) {
        onNodeRightClick(hit.id, e.clientX, e.clientY);
      }
    }

    // Attach listeners
    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('mousedown', handleMouseDown);
    canvas.addEventListener('wheel', handleWheel, { passive: false });
    canvas.addEventListener('contextmenu', handleContextMenu);

    // Cleanup
    return () => {
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('mousedown', handleMouseDown);
      canvas.removeEventListener('wheel', handleWheel);
      canvas.removeEventListener('contextmenu', handleContextMenu);
      if (zoomAnimRef.current !== null) cancelAnimationFrame(zoomAnimRef.current);
    };
  }, [canvasEl, simulationRef, nodesRef, adjacency, onNodeClick, onNodeRightClick]);

  const state: MapInteractionState = {
    transform,
    hoveredNodeId,
    draggedNodeId,
    isDragging,
    isPanning,
  };

  return {
    state,
    zoomIn,
    zoomOut,
    resetZoom,
    setTransform,
    focusOnNodes,
  };
}
